import {
    Box,
    Typography,
    useTheme,
    CircularProgress
} from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import { LineChart } from '@mui/x-charts/LineChart';
import PortalCard from '../../../../../components/PortalCard';
import analyticsService from '../../../../../services/analytics/AnalyticsService';
import { Moment } from 'moment';

interface ViewsStockCorrelationProps {
    dateRange?: [Moment, Moment];
}

const ViewsStockCorrelation: React.FC<ViewsStockCorrelationProps> = ({ dateRange }) => {
    const theme = useTheme();
    const [loading, setLoading] = React.useState(false);
    const [data, setData] = React.useState<Array<{
        productId: string;
        productName: string;
        stockOnHand: number;
        viewCount: number;
    }>>([]);
    const [error, setError] = React.useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const startDate = dateRange?.[0]?.toISOString();
                const endDate = dateRange?.[1]?.toISOString();
                const result = await analyticsService.getViewsStockCorrelation(startDate, endDate);
                if (result.success && result.data) {
                    setData(result.data);
                } else {
                    setError(result.message || 'Failed to fetch views stock correlation');
                }
            } catch (err: any) {
                setError(err.message || 'Failed to fetch views stock correlation');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [dateRange]);

    // Group data by stock ranges for better visualization
    const stockRanges = useMemo(() => {
        const ranges = [
            { label: '0-10', min: 0, max: 10 },
            { label: '11-30', min: 11, max: 30 },
            { label: '31-50', min: 31, max: 50 },
            { label: '51-100', min: 51, max: 100 },
            { label: '100+', min: 101, max: Infinity }
        ];

        return ranges.map(range => {
            const productsInRange = data.filter(item => 
                item.stockOnHand >= range.min && item.stockOnHand <= range.max
            );
            const avgViews = productsInRange.length > 0
                ? productsInRange.reduce((sum, item) => sum + item.viewCount, 0) / productsInRange.length
                : 0;

            return {
                label: range.label,
                avgViews: Math.round(avgViews * 10) / 10,
                productCount: productsInRange.length
            };
        });
    }, [data]);

    const labels = stockRanges.map(r => r.label);
    const avgViewsData = stockRanges.map(r => r.avgViews);

    return (
        <PortalCard
            sx={{
                p: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <Box sx={{ mb: 2 }}>
                <Typography variant='h6' fontFamily='primaryFont' fontWeight='bold' sx={{ mb: 0.5 }}>
                    Views vs Stock Correlation
                </Typography>
                <Typography variant='body2' color='text.secondary' fontFamily='primaryFont'>
                    Average views by stock level ranges
                </Typography>
            </Box>

            <Box sx={{ flexGrow: 1, minHeight: 400, width: '100%' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <Typography variant='body2' color='error' fontFamily='primaryFont'>
                            {error}
                        </Typography>
                    </Box>
                ) : data.length > 0 ? (
                    <>
                        <LineChart
                            xAxis={[{
                                data: labels,
                                scaleType: 'point',
                                label: 'Stock Level Range',
                                tickLabelStyle: {
                                    fontSize: '0.875rem',
                                    fill: theme.palette.text.primary,
                                    fontWeight: 500,
                                    fontFamily: 'primaryFont'
                                }
                            }]}
                            series={[{
                                data: avgViewsData,
                                label: 'Avg Views',
                                color: theme.palette.primary.main,
                                showMark: true,
                                curve: 'natural',
                            }]}
                            height={350}
                            margin={{ top: 20, right: 30, bottom: 60, left: 60 }}
                            slotProps={{
                                legend: { hidden: true }
                            }}
                            sx={{
                                '& .MuiChartsAxis-tickLabel': {
                                    fontSize: '0.875rem',
                                    fill: theme.palette.text.secondary,
                                    fontFamily: 'primaryFont',
                                },
                                '& .MuiChartsAxis-line': {
                                    stroke: theme.palette.divider,
                                },
                            }}
                        />
                        <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                            {stockRanges.map((range, index) => (
                                <Box key={index} sx={{ textAlign: 'center' }}>
                                    <Typography variant='caption' color='text.secondary' fontFamily='primaryFont'>
                                        {range.label}
                                    </Typography>
                                    <Typography variant='body2' fontFamily='primaryFont' fontWeight={600}>
                                        {range.avgViews.toFixed(1)} avg views
                                    </Typography>
                                    <Typography variant='caption' color='text.secondary' fontFamily='primaryFont'>
                                        {range.productCount} products
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </>
                ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <Typography variant='body2' color='text.secondary' fontFamily='primaryFont'>
                            No data available
                        </Typography>
                    </Box>
                )}
            </Box>
        </PortalCard>
    );
};

export default ViewsStockCorrelation;

